/**
 * https://wiki.openstreetmap.org/wiki/Zoom_levels
 *
 * Zoom levels scale by powers of two (each integer increment halves the ground tile width,
 * each decrement doubles it).
 *
 * In continuous camera zooming, nominal transitions occur at:
 * - Zoom-in threshold: targetTileWidth <= currentTileWidth / 2 (0.50x)
 * - Zoom-out threshold: targetTileWidth >= currentTileWidth (1.00x)
 *
 * To prevent rapid flip-flopping (hysteresis / chattering) when the camera zoom rests near
 * either boundary, we create an asymmetric deadband:
 * - ZOOM_IN_HYSTERESIS_FACTOR (0.42): Derived from 0.50 minus an ~16% buffer (approx. 2^-1.25),
 *   allowing the user to zoom slightly past the standard 0.5x threshold before forcing an ALKIS reload.
 * - ZOOM_OUT_HYSTERESIS_FACTOR (1.18): Derived from 1.00 plus an ~18% buffer (approx. 2^0.24),
 *   requiring deliberate zooming out past 1.00x before dropping to a coarser zoom level.
 */

export const MIN_MAP_ZOOM = 10;
export const MAX_MAP_ZOOM = 22;

export const ZOOM_IN_HYSTERESIS_FACTOR = 0.42;
export const ZOOM_OUT_HYSTERESIS_FACTOR = 1.18;

export const viewportWidthToZoom = (viewportWidth: number, currentZoom?: number): number => {
  // 1 Tile has 512x512px -> 4x4 Tiles -> 2048x2048 Texture;
  const targetTileWidth = Math.max(viewportWidth / 4, 1);

  // If currentZoom is provided, apply hysteresis to prevent flip-flopping near boundaries
  if (currentZoom !== undefined && currentZoom >= MIN_MAP_ZOOM && currentZoom <= MAX_MAP_ZOOM) {
    const currentTileWidth = 38 * Math.pow(2, 19 - currentZoom);
    // Hysteresis deadband: retain current zoom if within bounds to prevent jitter
    if (
      targetTileWidth > currentTileWidth * ZOOM_IN_HYSTERESIS_FACTOR &&
      targetTileWidth < currentTileWidth * ZOOM_OUT_HYSTERESIS_FACTOR
    ) {
      return currentZoom;
    }
  }

  // Start with zoom level of 19 which is roughly 38 meters
  let zoom = 19;
  let tileWidth = 38;

  // Scale up when zooming in closer (smaller viewportWidth)
  while (tileWidth / 2 >= targetTileWidth && zoom < MAX_MAP_ZOOM) {
    zoom += 1;
    tileWidth /= 2;
  }

  // Scale down when zooming out further (larger viewportWidth)
  while (tileWidth < targetTileWidth && zoom > MIN_MAP_ZOOM) {
    zoom -= 1;
    tileWidth *= 2;
  }

  return Math.max(MIN_MAP_ZOOM, Math.min(MAX_MAP_ZOOM, zoom));
};
